import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaseView } from '@chemoflow/contracts';
import { selectors } from '@chemoflow/domain';
import { Icon, IconSwap } from '../components/Icon.js';
import { DoneArticle, ListSection, PatientWho, cls, liveStatus } from '../components/Card.js';
import { useCardMotion } from '../lib/motion.js';
import { laterRecords, nurseStage } from '../lib/undo.js';
import { useBoardData } from '../state/board.js';
import { useClock } from '../state/clock.js';
import { useUi } from '../state/ui.js';

type ReasonMode = 'change' | 'stop';
const LABEL: Record<ReasonMode, string> = { change: '変更', stop: '中止' };

/** 変更・中止の理由は選択式（複数選べる）。詳しい内容はカルテを見てもらう。 */
const REASONS: Record<ReasonMode, readonly string[]> = {
  change: ['採血結果', '体重', '副作用', 'その他（カルテ参照）'],
  stop: ['採血結果', '副作用', 'その他（カルテ参照）'],
};
/** 複数選んだ理由は「・」でつないで1つの文字列として記録する（例：採血結果・副作用）。 */
const REASON_SEP = '・';
const NO_REASONS: readonly string[] = [];

interface TodoCardProps {
  case: CaseView;
  draft: readonly string[];
  onDraft(value: readonly string[]): void;
}

/** 理由のボタン。「その他（カルテ参照）」は補足を小さく出す。 */
function ReasonChip({ value, on, onToggle }: { value: string; on: boolean; onToggle(): void }) {
  const m = /^(.+?)（(.+)）$/.exec(value);
  return (
    <button className="chip" type="button" data-value={value} aria-pressed={on} onClick={onToggle}>
      <Icon name="check" />
      {m ? (
        <>
          {m[1]}
          <small>（{m[2]}）</small>
        </>
      ) : (
        value
      )}
    </button>
  );
}

/**
 * 入力前カード。確定はワンタッチ、変更・中止は理由を選んでから「◯◯を確定」で送る。
 * 選びかけの理由と入力モードは、状態が更新されても残す。
 */
function TodoCard({ case: c, draft, onDraft }: TodoCardProps) {
  const { act, isBusy } = useBoardData();
  const { confirmTap } = useUi();
  // 送信中は同じ案件のボタンを止める。連打の2回目は版が古くなり、
  // 自分の操作なのに「他の端末で状態が変わった」と返ってくる。
  // 見た目は確定済みHTMLが持つ無効ボタンの形（.btn.is-off）をそのまま使う。
  const busy = isBusy(c.caseId);
  const [mode, setMode] = useState<ReasonMode | null>(null);
  const reasonRef = useRef<HTMLDivElement>(null);
  // 閉じるアニメーションのあいだも中身を残すため、最後に開いていたモードを覚えておく（開くまでは何も置かない）
  const [shown, setShown] = useState<ReasonMode | null>(null);
  // いまのモードで選べる理由だけを数える（変更⇄中止を切り替えても、共通の理由は選ばれたまま残る）
  const picked = mode ? REASONS[mode].filter((r) => draft.includes(r)) : [];

  const openReason = (next: ReasonMode) => {
    setMode(next);
    setShown(next);
  };
  // 開いたら最初の理由にフォーカスを置く（キーボードだけでも選んで確定できる）
  useEffect(() => {
    if (mode)
      reasonRef.current?.querySelector<HTMLButtonElement>('.chip')?.focus({ preventScroll: true });
  }, [mode]);

  const toggle = (value: string) =>
    onDraft(draft.includes(value) ? draft.filter((r) => r !== value) : [...draft, value]);

  const submitReason = () => {
    const kind = mode;
    if (!kind || !picked.length) return;
    setMode(null);
    void act({ case: c, action: kind, reason: picked.join(REASON_SEP) }).then((ok) => {
      if (ok) onDraft([]);
    });
  };

  const onConfirm = async () => {
    const ok = await confirmTap({
      action: '調製確定',
      name: c.patient?.name ?? '',
      patientId: c.patient?.patientId ?? c.surrogateKey,
      dept: c.patient?.dept ?? '',
      color: 'var(--primary)',
    });
    if (!ok) return;
    setMode(null);
    void act({ case: c, action: 'confirm' });
  };

  const pending = mode ? `is-pending-${mode}` : '';

  return (
    <article
      className={`card ${mode ? 'is-editing' : ''}`.trim()}
      data-id={c.caseId}
      data-mode={mode ?? undefined}
    >
      <div className="row">
        <div className={`status ${pending}`}>{mode ? '入力中' : '入力前'}</div>
        <PatientWho case={c} />
        <div className="actions act">
          <button
            className={cls('btn', busy && 'is-off')}
            type="button"
            data-action="confirm"
            onClick={onConfirm}
            disabled={busy}
          >
            <IconSwap name="check" />
            確定
          </button>
          <button
            className={cls('btn', mode === 'change' && 'is-pending-change', busy && 'is-off')}
            type="button"
            data-action="change"
            onClick={() => (mode === 'change' ? setMode(null) : openReason('change'))}
            disabled={busy}
          >
            <IconSwap name="autorenew" to="border_color" />
            変更
          </button>
          <button
            className={cls('btn', mode === 'stop' && 'is-pending-stop', busy && 'is-off')}
            type="button"
            data-action="stop"
            onClick={() => (mode === 'stop' ? setMode(null) : openReason('stop'))}
            disabled={busy}
          >
            <IconSwap name="close" to="border_color" />
            中止
          </button>
        </div>
      </div>
      <div className="reason-wrap">
        <div className="reason-inner" inert={!mode}>
          <div
            className={`reason ${shown === 'stop' ? 'is-stop' : ''}`.trim()}
            role="group"
            aria-label={shown ? `${LABEL[shown]}の理由` : undefined}
            ref={reasonRef}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMode(null);
            }}
          >
            {shown && (
              <>
                {REASONS[shown].map((r) => (
                  <ReasonChip key={r} value={r} on={draft.includes(r)} onToggle={() => toggle(r)} />
                ))}
                <button
                  className="submit"
                  type="button"
                  onClick={submitReason}
                  disabled={busy || !picked.length}
                >
                  {LABEL[shown]}を確定
                  <Icon name="arrow_forward" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/** 入力済みカード。確定以外は理由を添えて出す。 */
function DoneCard({ case: c, now, onUndo }: { case: CaseView; now: number; onUndo(): void }) {
  if (!c.clerk) return null;
  const { type, reason, at } = c.clerk;
  const live = liveStatus(c, now);
  return (
    <DoneArticle
      case={c}
      tone={live.cls}
      pill={live.node}
      label="入力時間"
      ts={at}
      onUndo={onUndo}
      middle={
        type === 'confirm' ? null : (
          <div className={`note ${type === 'stop' ? 'is-stop' : ''}`.trim()} title={reason}>
            <p className="clamp2">{reason}</p>
          </div>
        )
      }
    />
  );
}

/** 外来クラーク：まだ確定を伝えていない患者は誰か。 */
export function ClerkView() {
  const { cases, undo } = useBoardData();
  const { now } = useClock();
  const { ask } = useUi();
  const viewRef = useRef<HTMLElement>(null);
  const [drafts, setDrafts] = useState<Record<string, readonly string[]>>({});

  const todo = selectors.clerkTodo(cases);
  const done = selectors.clerkDone(cases);
  useCardMotion(viewRef, cases);

  const setDraft = useCallback(
    (caseId: string, value: readonly string[]) => setDrafts((d) => ({ ...d, [caseId]: value })),
    [],
  );

  /**
   * 取消。調製・投与まで進んでいる場合は必ず確認を挟み、一緒に消える記録を示す。
   * 選んでいた理由は取消後のカードに残しておく。
   */
  const onUndo = useCallback(
    async (c: CaseView) => {
      const name = c.patient?.name ?? 'この患者';
      if (c.admin) {
        const ok = await ask({
          title: `${name} さんは化学療法室で${nurseStage(c)}済みです`,
          text: `戻すと、${laterRecords(c, 'pharm')}の記録も取り消されます。`,
          ok: '戻す',
          tone: 'danger',
        });
        if (!ok) return;
      } else if (c.pharm) {
        const ok = await ask({
          title: `${name} さんは薬剤部で${c.pharmDone ? '調製完了' : '調製開始'}済みです`,
          text: `戻すと${laterRecords(c, 'pharm')}の記録も取り消されます。\n変更・中止を入力し直した場合は、薬剤部へ電話でも伝えてください。`,
          ok: '戻す',
        });
        if (!ok) return;
      }
      const reason = c.clerk?.reason ?? '';
      const done2 = await undo(c);
      if (done2 && reason) setDraft(c.caseId, reason.split(REASON_SEP));
    },
    [ask, undo, setDraft],
  );

  return (
    <main className="board is-clerk view" id="view-clerk" data-view="clerk" ref={viewRef}>
      <section>
        <ListSection title="入力前" count={todo.length} emptyText="すべて入力済みです">
          {todo.map((c) => (
            <TodoCard
              key={c.caseId}
              case={c}
              draft={drafts[c.caseId] ?? NO_REASONS}
              onDraft={(v) => setDraft(c.caseId, v)}
            />
          ))}
        </ListSection>
      </section>
      <section>
        <ListSection title="入力済み" count={done.length} emptyText="まだ入力はありません">
          {done.map((c) => (
            <DoneCard key={c.caseId} case={c} now={now} onUndo={() => void onUndo(c)} />
          ))}
        </ListSection>
      </section>
    </main>
  );
}
